import React, { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CourseViewer } from '../../components';
import Bugsnag from '@bugsnag/js';
import ExpiredContentWarning from 'components/ExpiredContentWarning';
import { type Booking as BookingRecord, Course, Educator, Occurrence } from '@shared/types';
import { Temporal } from 'temporal-polyfill';
const { Spinner, request, toaster } = require('cademy-shared-web');

type GetCourseLessonsResponse = {
    access_expired: boolean;
    access_expiry_date: string;
    course: Course;
    educator: Educator;
    booking: BookingRecord;
    occurrence: Occurrence;
};

export const Booking: FunctionComponent<{ subdomain?: string }> = ({ subdomain }) => {
    const { booking_param } = useParams<{ booking_param: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<GetCourseLessonsResponse | null>(null);

    useEffect(() => {
        request
            .get(`/get-course-lessons/booking/${booking_param}`, { handleErrors: false })
            .then(({ data }: { data: GetCourseLessonsResponse }) => {
                setData(data);
            })
            .catch((error: unknown) => {
                console.error(error);
                if (
                    process.env.REACT_APP_ENVIRONMENT === 'Production' ||
                    process.env.REACT_APP_ENVIRONMENT === 'Stage'
                ) {
                    Bugsnag.notify(error as Error);
                }
                toaster.danger('There was an issue loading the booking');
                window.location.href = '/profile';
            })
            .then(() => {
                setLoading(false);
            });
    }, [booking_param]);

    if (loading || data === null) return <Spinner />;
    if (data.access_expired) {
        const domain = window.location.origin;
        const contactLinkParams = new URLSearchParams({
            type: 'message',
            educator_slug: data.educator.slug,
            educator_name: data.educator.name,
            course_ref: data.course.slug,
            course_name: data.course.name,
        }).toString();
        const expiryDate = Temporal.ZonedDateTime.from(data.access_expiry_date);
        return (
            <ExpiredContentWarning
                expiryDate={expiryDate}
                courseKeyword={data.course.keyword || 'course'}
                recordType="booking"
                receiptLink={
                    subdomain
                        ? `${domain}/${data.course.slug}/details/${data.occurrence._id}?booking=${data.booking._id}`
                        : `${domain}/${data.educator.slug}/${data.course.slug}/details/${data.occurrence._id}?booking=${data.booking._id}`
                }
                courseLink={
                    subdomain
                        ? `${domain}/${data.course.slug}`
                        : `${domain}/${data.educator.slug}/${data.course.slug}`
                }
                contactLink={
                    subdomain
                        ? `${domain}/enquiry?${contactLinkParams}`
                        : `${domain}/${data.educator.slug}/enquiry?${contactLinkParams}`
                }
            />
        );
    }

    return <CourseViewer data={data} setData={setData} />;
};

export default Booking;
